import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  CircularProgress,
  Typography,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface PdfPreviewModalProps {
  open: boolean;
  onClose: () => void;
  pdfUrl: string;
}

const PdfPreviewModal: React.FC<PdfPreviewModalProps> = ({
  open,
  onClose,
  pdfUrl,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  // Create a safe URL for iframe
  const createViewerUrl = (url: string) => {
    return `https://docs.google.com/viewer?url=${encodeURIComponent(
      url
    )}&embedded=true`;
  };

  const handleLoad = () => setLoading(false);
  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        style: {
          height: "90vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>PDF Preview</span>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, position: "relative" }}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error ? (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              p: 2,
              zIndex: 1,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Failed to load preview
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setLoading(true);
                setError(false);
              }}
              sx={{ mt: 2 }}
            >
              Retry
            </Button>
            <Button
              variant="outlined"
              href={pdfUrl}
              target="_blank"
              rel="noopener"
              sx={{ mt: 2 }}
            >
              Download PDF
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              height: "100%",
              width: "100%",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <iframe
              src={createViewerUrl(pdfUrl)}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              title="PDF Preview"
              onLoad={handleLoad}
              onError={handleError}
              sandbox="allow-scripts allow-same-origin"
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PdfPreviewModal;
